import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, ImagePlus, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-foreground/15 bg-background px-3 py-2.5 text-sm text-foreground outline-none transition-colors duration-300 placeholder:text-muted-foreground/50 focus:border-primary";
const labelCls =
  "mb-1.5 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground";

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Fehler.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const waDigits = (phone) => {
  let d = String(phone || "").replace(/\D/g, "");
  if (d.startsWith("0")) d = `49${d.slice(1)}`;
  return d;
};

const waLink = (phone) => `https://wa.me/${waDigits(phone)}`;

const Field = ({ label, value, onChange, textarea = false, rows = 3, type = "text" }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {textarea ? (
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y`} />
    ) : (
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    )}
  </div>
);

export const SeoPanel = ({ draft, set }) => (
  <div className="max-w-2xl space-y-5" data-testid="admin-panel-seo">
    <p className="border border-foreground/10 bg-card p-4 text-xs leading-relaxed text-muted-foreground">
      Diese Angaben steuern den Seitentitel (Browser-Tab & Google) und die Beschreibung in den
      Suchergebnissen. Titel ca. 50–60 Zeichen, Beschreibung ca. 150–160 Zeichen.
    </p>
    <Field label="Seitentitel" value={draft.seo.title} onChange={(v) => set(["seo", "title"], v)} />
    <Field label="Meta-Beschreibung" textarea rows={3} value={draft.seo.description} onChange={(v) => set(["seo", "description"], v)} />
    <Field label="Keywords (Komma-getrennt)" value={draft.seo.keywords} onChange={(v) => set(["seo", "keywords"], v)} />
  </div>
);

const emptyForm = {
  customer_email: "",
  customer_name: "",
  customer_phone: "",
  title: "",
  description: "",
  status: "in_arbeit",
  address: "",
  next_appointment: "",
  steps: [],
  updates: [],
};

const STATUS_LABELS = {
  anfrage: "Anfrage",
  in_arbeit: "In Arbeit",
  pausiert: "Pausiert",
  fertig: "Fertig",
};

export const ProjectsPanel = ({ authHeaders }) => {
  const [projects, setProjects] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState({});
  const [waText, setWaText] = useState("");
  const [copied, setCopied] = useState("");

  const rawToken = (authHeaders.Authorization || "").replace("Bearer ", "");

  const buildWaText = (f) => {
    if (!f) return "";
    const done = (f.steps || []).filter((s) => s.done).length;
    const latest = (f.updates || []).filter((u) => (u.text || "").trim()).slice(-1)[0];
    const magicUrl = f.magic_token ? `${window.location.origin}/p/${f.magic_token}` : "";
    const lines = [
      `Moin${f.customer_name ? ` ${f.customer_name}` : ""}! Kurzes Update zu deinem Projekt „${f.title}":`,
      `Status: ${STATUS_LABELS[f.status] || f.status}${(f.steps || []).length ? ` (${done}/${f.steps.length} Schritte erledigt)` : ""}`,
    ];
    if (f.next_appointment) lines.push(`Nächster Termin: ${f.next_appointment}`);
    if (latest) lines.push(`Neueste Meldung: ${latest.text}`);
    if (magicUrl) lines.push(`Alle Details & Fotos: ${magicUrl}`);
    lines.push("Liebe Grüße, John (Rundum)");
    return lines.join("\n");
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (editing && form) setWaText(buildWaText(form));
  }, [editing]);

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(""), 2000);
    });
  };

  const load = () => {
    fetch(`${API}/admin/projects`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : { projects: [] }))
      .then((d) => setProjects(d.projects));
    fetch(`${API}/admin/customers`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : { customers: [] }))
      .then((d) => setCustomers(d.customers));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const flash = (t) => {
    setMsg(t);
    setTimeout(() => setMsg(""), 3000);
  };

  const save = async () => {
    const isNew = editing === "new";
    const r = await fetch(isNew ? `${API}/admin/projects` : `${API}/admin/projects/${editing}`, {
      method: isNew ? "POST" : "PUT",
      headers: { ...authHeaders, "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    const data = await r.json().catch(() => ({}));
    if (r.ok) {
      flash("Gespeichert.");
      setEditing(null);
      load();
    } else {
      flash(formatApiErrorDetail(data.detail));
    }
  };

  const remove = async (id) => {
    await fetch(`${API}/admin/projects/${id}`, { method: "DELETE", headers: authHeaders });
    load();
  };

  const setStep = (i, key, v) =>
    setForm((f) => ({ ...f, steps: f.steps.map((s, j) => (j === i ? { ...s, [key]: v } : s)) }));
  const setUpdate = (i, key, v) =>
    setForm((f) => ({ ...f, updates: f.updates.map((u, j) => (j === i ? { ...u, [key]: v } : u)) }));

  const uploadPhoto = async (i, file) => {
    setUploading((s) => ({ ...s, [i]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API}/admin/upload-photo`, {
        method: "POST",
        headers: authHeaders,
        body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setForm((f) => ({
          ...f,
          updates: f.updates.map((u, j) =>
            j === i ? { ...u, photos: [...(u.photos || []), d.path] } : u
          ),
        }));
      } else {
        flash(d.detail || "Foto-Upload fehlgeschlagen");
      }
    } catch {
      flash("Foto-Upload fehlgeschlagen");
    }
    setUploading((s) => ({ ...s, [i]: false }));
  };

  const removePhoto = (i, pi) =>
    setForm((f) => ({
      ...f,
      updates: f.updates.map((u, j) =>
        j === i ? { ...u, photos: (u.photos || []).filter((_, k) => k !== pi) } : u
      ),
    }));

  return (
    <div data-testid="admin-panel-projects">
      <div className="mb-6 flex items-center justify-between">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
          {projects ? `${projects.length} Projekt(e)` : "Lade …"}
        </p>
        <button
          onClick={() => {
            setForm(structuredClone(emptyForm));
            setEditing("new");
          }}
          data-testid="project-new-button"
          className="inline-flex items-center gap-2 bg-primary px-4 py-2 font-heading text-xs font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
        >
          <Plus className="h-4 w-4" /> Neues Projekt
        </button>
      </div>

      {msg && <p className="mb-4 text-sm text-primary" data-testid="projects-message">{msg}</p>}

      {editing && form && (
        <div className="mb-8 space-y-5 border border-primary/40 bg-card p-6" data-testid="project-form">
          <div className="flex items-center justify-between">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
              {editing === "new" ? "Neues Projekt" : "Projekt bearbeiten"}
            </p>
            <button onClick={() => setEditing(null)} className="text-muted-foreground transition-colors duration-300 hover:text-primary" aria-label="Schließen">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div>
            <label className={labelCls}>Kunden-Konto verknüpfen (freiwillig)</label>
            {customers.length > 0 ? (
              <select
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                className={inputCls}
                data-testid="project-customer-select"
              >
                <option value="">– Ohne Konto (nur WhatsApp reicht) –</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.email}>
                    {c.name ? `${c.name} (${c.email})` : c.email}
                  </option>
                ))}
              </select>
            ) : (
              <input
                value={form.customer_email}
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                className={inputCls}
                placeholder="Optional: E-Mail eines registrierten Kunden"
                data-testid="project-customer-select"
              />
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Kunden-Name" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} />
            <Field label="Handynummer des Kunden (für WhatsApp)" value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} />
          </div>

          <Field label="Projekt-Titel" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
          <Field label="Beschreibung" textarea rows={3} value={form.description} onChange={(v) => setForm({ ...form, description: v })} />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={labelCls}>Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="project-status-select">
                <option value="anfrage">Anfrage</option>
                <option value="in_arbeit">In Arbeit</option>
                <option value="pausiert">Pausiert</option>
                <option value="fertig">Fertig</option>
              </select>
            </div>
            <Field label="Nächster Termin (freier Text)" value={form.next_appointment} onChange={(v) => setForm({ ...form, next_appointment: v })} />
          </div>
          <Field label="Adresse / Baustelle" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />

          <div>
            <p className={labelCls}>Arbeitsschritte (Zeitleiste)</p>
            <div className="space-y-2">
              {form.steps.map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={s.done}
                    onChange={(e) => setStep(i, "done", e.target.checked)}
                    className="h-4 w-4 shrink-0 accent-[hsl(15,80%,54%)]"
                    aria-label="Erledigt"
                  />
                  <input value={s.title} onChange={(e) => setStep(i, "title", e.target.value)} className={inputCls} placeholder={`Schritt ${i + 1}`} />
                  <button onClick={() => setForm((f) => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }))} className="shrink-0 text-muted-foreground transition-colors duration-300 hover:text-primary" aria-label="Schritt löschen">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setForm((f) => ({ ...f, steps: [...f.steps, { title: "", done: false }] }))}
                className="inline-flex items-center gap-2 border border-foreground/15 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                data-testid="project-add-step"
              >
                <Plus className="h-3.5 w-3.5" /> Schritt hinzufügen
              </button>
            </div>
          </div>

          <div>
            <p className={labelCls}>Berichte, Termine & Dokumente</p>
            <div className="space-y-3">
              {form.updates.map((u, i) => (
                <div key={i} className="space-y-2 border border-foreground/10 p-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <input type="date" value={u.date} onChange={(e) => setUpdate(i, "date", e.target.value)} className={`${inputCls} w-auto`} aria-label="Datum" />
                    <select value={u.kind} onChange={(e) => setUpdate(i, "kind", e.target.value)} className={`${inputCls} w-auto`} aria-label="Art">
                      <option value="bericht">Bericht</option>
                      <option value="termin">Termin</option>
                      <option value="dokument">Dokument</option>
                    </select>
                    <button onClick={() => setForm((f) => ({ ...f, updates: f.updates.filter((_, j) => j !== i) }))} className="shrink-0 text-muted-foreground transition-colors duration-300 hover:text-primary" aria-label="Eintrag löschen">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <input value={u.text} onChange={(e) => setUpdate(i, "text", e.target.value)} className={inputCls} placeholder="Text, z. B. 'Trockenbau EG abgeschlossen'" />
                  {u.kind === "dokument" && (
                    <input value={u.link || ""} onChange={(e) => setUpdate(i, "link", e.target.value)} className={inputCls} placeholder="Link zum Dokument (https://…)" />
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    {(u.photos || []).map((ph, pi) => (
                      <div key={pi} className="relative">
                        <img
                          src={`${API}/files/${ph}?auth=${rawToken}`}
                          alt={`Baustellen-Foto ${pi + 1}`}
                          className="h-16 w-24 border border-foreground/10 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(i, pi)}
                          className="absolute -right-2 -top-2 rounded-full bg-primary p-0.5 text-primary-foreground"
                          aria-label="Foto entfernen"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <label className="inline-flex cursor-pointer items-center gap-2 border border-foreground/15 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary">
                      <ImagePlus className="h-3.5 w-3.5" />
                      {uploading[i] ? "Lädt …" : "Fotos hinzufügen"}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        data-testid={`project-photo-upload-${i}`}
                        onChange={(e) => Array.from(e.target.files).forEach((file) => uploadPhoto(i, file))}
                      />
                    </label>
                  </div>
                </div>
              ))}
              <button
                onClick={() => setForm((f) => ({ ...f, updates: [...f.updates, { date: "", kind: "bericht", text: "", link: "", photos: [] }] }))}
                className="inline-flex items-center gap-2 border border-foreground/15 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                data-testid="project-add-update"
              >
                <Plus className="h-3.5 w-3.5" /> Eintrag hinzufügen
              </button>
            </div>
          </div>

          <div className="space-y-3 border border-foreground/10 bg-background p-4" data-testid="whatsapp-send-panel">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">WhatsApp-Update an den Kunden</p>
            {form.magic_token ? (
              <>
                <div className="flex flex-wrap items-center gap-2 border border-foreground/10 p-3">
                  <p className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground" data-testid="magic-link">
                    {window.location.origin}/p/{form.magic_token}
                  </p>
                  <button
                    type="button"
                    onClick={() => copyText(`${window.location.origin}/p/${form.magic_token}`, "link")}
                    className="border border-foreground/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                    data-testid="magic-link-copy"
                  >
                    {copied === "link" ? "Kopiert!" : "Link kopieren"}
                  </button>
                </div>
                <textarea
                  rows={7}
                  value={waText}
                  onChange={(e) => setWaText(e.target.value)}
                  className={`${inputCls} resize-y`}
                  data-testid="wa-text"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setWaText(buildWaText(form))}
                    className="border border-foreground/15 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                    data-testid="wa-regenerate"
                  >
                    Text neu generieren
                  </button>
                  <button
                    type="button"
                    onClick={() => copyText(waText, "text")}
                    className="border border-foreground/15 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                    data-testid="wa-copy"
                  >
                    {copied === "text" ? "Kopiert!" : "Text kopieren"}
                  </button>
                  {form.customer_phone ? (
                    <a
                      href={`${waLink(form.customer_phone)}?text=${encodeURIComponent(waText)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="wa-send-button"
                      className="inline-flex items-center gap-2 bg-primary px-4 py-2 font-heading text-xs font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
                    >
                      <MessageCircle className="h-4 w-4" /> Per WhatsApp senden
                    </a>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Handynummer oben eintragen, um direkt per WhatsApp zu senden.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                Projekt erst speichern – dann bekommst du hier den Direktlink (Magic Link) und den fertigen WhatsApp-Text.
              </p>
            )}
          </div>

          <button
            onClick={save}
            data-testid="project-save-button"
            className="bg-primary px-6 py-3 font-heading text-xs font-bold uppercase tracking-wider text-primary-foreground transition-colors duration-300 hover:bg-foreground hover:text-background"
          >
            Projekt speichern
          </button>
        </div>
      )}

      {projects && projects.length > 0 && (
        <div className="space-y-3">
          {projects.map((p) => {
            const done = (p.steps || []).filter((s) => s.done).length;
            return (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 border border-foreground/10 bg-card p-4" data-testid={`project-row-${p.id}`}>
                <div>
                  <p className="font-heading text-base font-bold text-foreground">{p.title}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {p.customer_email} · {(p.steps || []).length ? `${done}/${p.steps.length} Schritte` : "keine Schritte"} · {p.status}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setForm(structuredClone(p));
                      setEditing(p.id);
                    }}
                    data-testid={`project-edit-${p.id}`}
                    className="rounded-full border border-foreground/15 p-2 text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                    aria-label="Bearbeiten"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`project-delete-${p.id}`}
                    className="rounded-full border border-foreground/15 p-2 text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                    aria-label="Löschen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const CustomersPanel = ({ authHeaders }) => {
  const [customers, setCustomers] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetch(`${API}/admin/customers`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : { customers: [] }))
      .then((d) => setCustomers(d.customers));
  }, []);

  return (
    <div data-testid="admin-panel-customers">
      {customers === null ? (
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Lade …</p>
      ) : customers.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="customers-empty">
          Noch keine Kunden registriert. Kunden können sich unter /portal selbst ein Konto erstellen.
        </p>
      ) : (
        <div className="space-y-3">
          {customers.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 border border-foreground/10 bg-card p-4" data-testid={`customer-row-${c.id}`}>
              <div>
                <p className="font-heading text-base font-bold text-foreground">{c.name || "–"}</p>
                <p className="font-mono text-xs text-primary">{c.email}</p>
                {c.phone && <p className="font-mono text-xs text-muted-foreground">{c.phone}</p>}
              </div>
              <div className="flex items-center gap-3">
                {c.phone && (
                  <a
                    href={waLink(c.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`customer-whatsapp-${c.id}`}
                    className="inline-flex items-center gap-2 border border-foreground/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                  </a>
                )}
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  seit {new Date(c.created_at).toLocaleDateString("de-DE")}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MessagesPanel = ({ authHeaders }) => {
  const [messages, setMessages] = useState(null);

  const load = () => {
    fetch(`${API}/admin/messages`, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => {
        setMessages(d.messages);
        if (d.messages.some((m) => !m.read)) {
          fetch(`${API}/admin/messages/read-all`, { method: "POST", headers: authHeaders });
        }
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    await fetch(`${API}/admin/messages/${id}`, { method: "DELETE", headers: authHeaders });
    setMessages((list) => list.filter((m) => m.id !== id));
  };

  return (
    <div data-testid="admin-panel-messages">
      {messages === null ? (
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Lade …</p>
      ) : messages.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="messages-empty">Noch keine Portal-Nachrichten.</p>
      ) : (
        <div className="space-y-3">
          {messages.map((m) => (
            <div key={m.id} className="border border-foreground/10 bg-card p-4" data-testid={`message-row-${m.id}`}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="font-heading text-sm font-bold text-foreground">
                  {m.user_name || m.user_email}
                  <span className="ml-2 font-mono text-[10px] font-normal uppercase tracking-[0.2em] text-muted-foreground">{m.user_email}</span>
                </p>
                <div className="flex items-center gap-3">
                  {m.user_phone && (
                    <a
                      href={waLink(m.user_phone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`message-whatsapp-${m.id}`}
                      className="inline-flex items-center gap-2 border border-foreground/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                    >
                      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                    </a>
                  )}
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {new Date(m.created_at).toLocaleString("de-DE")}
                  </p>
                  <button
                    onClick={() => remove(m.id)}
                    data-testid={`message-delete-${m.id}`}
                    className="rounded-full border border-foreground/15 p-1.5 text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                    aria-label="Nachricht löschen"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="whitespace-pre-line text-sm text-muted-foreground">{m.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
